import { memo, useMemo, type KeyboardEvent } from "react";
import { AlertTriangle, Clock, Eye, Loader2, User } from "lucide-react";

import { Badge } from "@/modern/components/ui/badge";
import { Card } from "@/modern/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";
import { MIXED_TASK_COLOR } from "@/other/constants/TaskTypes.jsx";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import bgContrastHelper from "@/other/utils/bgUtils.jsx";

import { getNotificationConfig } from "../constants/config";
import { TILE_SURFACE_FILL_CLS, toneStyle } from "../constants/tones";
import type { Notification, NotificationConfigContext } from "../types";
import { NotificationActions } from "./NotificationActions";

type TileState = "failed" | "pending" | "unread" | null;

export type NotificationMosaicLayout = "hero" | "half" | "cell";

function formatRelativeTime(value: string): string {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return "Recently";
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(new Date(timestamp));
}

function contrastColor(background: string): string {
  return bgContrastHelper(background, "#ffffff", "#111111") as string;
}

function trimName(value: string | null | undefined): string | null {
  const name = value?.trim();
  return name || null;
}

function getTaskRecord(taskId: number, context: NotificationConfigContext) {
  return (
    context.project?.getCrudStore?.(STORE_KEYS.taskStore)?.getById?.(taskId) ??
    context.project?.extraTaskStore?.getById?.(taskId) ??
    null
  );
}

function resolveTaskColor(task: { color?: unknown } | null): string | null {
  if (!task) return null;
  const raw = typeof task.color === "string" ? task.color.trim() : "";
  if (!raw || raw === MIXED_TASK_COLOR) return null;
  if (!raw.startsWith("#") && !/^[0-9a-f]{6}$/i.test(raw)) return null;
  return raw.startsWith("#") ? raw : `#${raw}`;
}

function tileState(notification: Notification): TileState {
  if (
    notification.async_task?.status === "Failed" ||
    notification.status === "Failed"
  ) {
    return "failed";
  }
  if (notification.async_task?.status === "Pending") return "pending";
  if (!notification.is_seen) return "unread";
  return null;
}

function tileTitle(
  notification: Notification,
  context: NotificationConfigContext,
): string {
  if (notification.task != null) {
    const task = getTaskRecord(notification.task, context);
    const fromApi = trimName(notification.task_name);
    const fromStore =
      typeof task?.name === "string" ? trimName(task.name) : null;
    const name = fromApi ?? fromStore;
    if (name) return name;
  }
  if (notification.resource != null) {
    const name = trimName(notification.resource_name);
    if (name) return name;
  }
  if (notification.client != null) {
    const name = trimName(notification.client_name);
    if (name) return name;
  }
  if (notification.checklist != null) {
    const name = trimName(notification.checklist_name);
    if (name) return name;
  }

  const entry = getNotificationConfig(notification);
  const raw = entry.getTitle(notification, context).trim();
  const prefix = `${entry.label}: `;
  if (raw.startsWith(prefix)) return raw.slice(prefix.length).trim() || raw;
  if (raw && raw !== entry.label) return raw;
  const description = entry.getDescription(notification, context).trim();
  if (description && description !== entry.label) return description;
  return raw || entry.label;
}

const BADGE_CLS =
  "pointer-events-none h-4 min-h-0 shrink-0 px-1.5 py-0 !text-[10px] leading-none font-normal";

function StateBadge({ state }: { state: TileState }) {
  if (!state) return null;
  if (state === "failed") {
    return (
      <Badge variant="destructive" size="sm" className={BADGE_CLS}>
        Failed
      </Badge>
    );
  }
  if (state === "pending") {
    return (
      <Badge
        variant="outline"
        size="sm"
        className={cn(
          BADGE_CLS,
          "border-transparent bg-warning text-warning-foreground",
        )}
      >
        Pending
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      size="sm"
      className={cn(BADGE_CLS, "border-0 bg-black/20 text-current")}
    >
      Unread
    </Badge>
  );
}

function stateEdgeClass(state: TileState): string {
  if (state === "failed") return "border-l-4 border-l-destructive";
  if (state === "pending") return "border-l-4 border-l-warning";
  if (state === "unread") return "border-l-4 border-l-black/40";
  return "border-l-4 border-l-transparent";
}

function MarkSeenButton({
  notification,
  context,
}: {
  notification: Notification;
  context: NotificationConfigContext;
}) {
  if (notification.is_seen || notification.async_task?.status === "Pending") {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex size-7 items-center justify-center rounded-md opacity-80 transition-colors duration-150 hover:bg-black/15 hover:opacity-100"
          aria-label="Mark as seen"
          onClick={(event) => {
            event.stopPropagation();
            void Promise.resolve(context.markSeen(notification));
          }}
        >
          <Eye className="size-3.5" aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">Mark as seen</TooltipContent>
    </Tooltip>
  );
}

function ObjectMosaicTile({
  notification,
  context,
  layout = "cell",
  className,
}: {
  notification: Notification;
  context: NotificationConfigContext;
  layout?: NotificationMosaicLayout;
  className?: string;
}) {
  const entry = getNotificationConfig(notification);
  const Icon = entry.icon;
  const actor = entry.getActorName?.(notification, context) ?? "Swivu";
  const description = entry.getDescription(notification, context);
  const state = tileState(notification);
  const pending = state === "pending";
  const failed = state === "failed";
  const title = tileTitle(notification, context);
  const task =
    notification.task != null
      ? getTaskRecord(notification.task, context)
      : null;
  const taskColor = resolveTaskColor(task);
  const hexForeground = taskColor ? contrastColor(taskColor) : undefined;
  const categoryStyle = taskColor
    ? undefined
    : toneStyle(entry.classes.bg);

  const actions = useMemo(
    () => entry.getActions(notification, context),
    [context, entry, notification],
  );

  function open() {
    entry.onOpen(notification, context);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      open();
    }
  }

  return (
    <Card
      variant="mosaic"
      role="button"
      tabIndex={0}
      aria-label={title}
      onClick={open}
      onKeyDown={onKeyDown}
      style={
        taskColor
          ? { backgroundColor: taskColor, color: hexForeground }
          : categoryStyle
      }
      className={cn(
        "group relative flex h-full min-h-0 cursor-pointer flex-col overflow-hidden rounded-[1.5rem] p-5 shadow-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        stateEdgeClass(state),
        !taskColor && TILE_SURFACE_FILL_CLS,
        !taskColor && "text-white",
        className,
      )}
    >
      <div
        className={cn(
          "pointer-events-none absolute right-3 top-3 z-10 flex items-center gap-0.5 opacity-0 transition-opacity duration-150",
          "group-hover:pointer-events-auto group-hover:opacity-100",
          "group-focus-within:pointer-events-auto group-focus-within:opacity-100",
          "[@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100",
        )}
      >
        <MarkSeenButton notification={notification} context={context} />
        <NotificationActions actions={actions} />
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-w-0 items-center gap-1.5 pr-8">
          <span className="flex shrink-0 items-center opacity-80">
            {failed ? (
              <AlertTriangle className="size-4" aria-hidden />
            ) : pending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Icon className="size-4" aria-hidden />
            )}
          </span>
          <p className="m-0 min-w-0 truncate text-xs font-medium uppercase tracking-wide opacity-80">
            {entry.label}
          </p>
        </div>

        <div className="mt-1.5 min-h-0 space-y-0.5">
          <p
            title={title}
            className={cn(
              "font-bold leading-snug tracking-tight",
              layout === "hero"
                ? "line-clamp-2 text-2xl"
                : layout === "half"
                  ? "line-clamp-2 text-xl"
                  : "line-clamp-2 text-lg",
            )}
          >
            {title}
          </p>
          {description ? (
            <p
              title={description}
              className={cn(
                "text-sm font-normal leading-snug opacity-80",
                layout === "hero"
                  ? "line-clamp-3"
                  : "line-clamp-2",
              )}
            >
              {description}
            </p>
          ) : null}
        </div>
      </div>

      <div className="mt-auto flex min-w-0 items-center gap-2 pt-2">
        <StateBadge state={state} />
        <div className="flex min-w-0 flex-1 items-center gap-2.5 overflow-hidden text-sm font-normal leading-none opacity-80">
          <span className="inline-flex min-w-0 items-center gap-1">
            <User className="size-3.5 shrink-0" aria-hidden />
            <span className="truncate">{actor}</span>
          </span>
          <span className="inline-flex shrink-0 items-center gap-1">
            <Clock className="size-3.5" aria-hidden />
            <span className="whitespace-nowrap">
              {formatRelativeTime(notification.alert_date)}
            </span>
          </span>
        </div>
      </div>
    </Card>
  );
}

export const NotificationTile = memo(function NotificationTile({
  notification,
  context,
  layout,
  className,
}: {
  notification: Notification;
  context: NotificationConfigContext;
  layout?: NotificationMosaicLayout;
  className?: string;
}) {
  const entry = getNotificationConfig(notification);
  const tile = (
    <ObjectMosaicTile
      notification={notification}
      context={context}
      layout={layout}
      className={className}
    />
  );

  return entry.tileRenderer
    ? entry.tileRenderer({
        notification,
        context,
        entry,
        defaultTile: tile,
        actions: (
          <NotificationActions actions={entry.getActions(notification, context)} />
        ),
      })
    : tile;
});
